import { useState } from "react";
import { Dropdown } from "antd";
import { get, keyBy } from "lodash";
import { useQuery } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";
import CanvasIcon from "./CanvasIcon";
import ConnectorField from "./ConnectorField";
import ConnectorMappings from "./ConnectorMappings";
import FailureModeField from "./FailureModeField";
import MappingList from "./MappingList";
import { ICON_PATHS } from "./step-look";

const ConnectorListField = ({
  value,
  onChange,
  labels,
  failureLabels,
  listLabels,
}) => {
  const items = value || [];
  const { data } = useQuery(graphql.queries.CONNECTORS);
  const connectorByKey = keyBy(get(data, "connectors") || [], "key");
  const [editing, setEditing] = useState({});

  const update = (index, patch) =>
    onChange(
      items.map((item, i) => (i === index ? { ...item, ...patch } : item)),
    );

  const setConfig = (index, patch) =>
    update(index, { config: { ...(items[index].config || {}), ...patch } });

  const remove = (index) => onChange(items.filter((_, i) => i !== index));

  const add = () => {
    setEditing((current) => ({ ...current, [items.length]: true }));
    onChange([...items, { connectorKey: null, config: {} }]);
  };

  const toggle = (index) =>
    setEditing((current) => ({ ...current, [index]: !current[index] }));

  return (
    <div className="canvas-rows">
      {items.length === 0 && listLabels.EMPTY ? (
        <p className="canvas-tab__hint">{listLabels.EMPTY}</p>
      ) : null}
      {items.map((item, index) => {
        const connector = connectorByKey[item.connectorKey];
        return (
          <div key={index} className="canvas-row-card">
            <div className="canvas-row-card__head">
              <span className="canvas-row-card__icon canvas-row-card__icon--connector">
                <CanvasIcon path={ICON_PATHS.plug} size={16} strokeWidth={2} />
              </span>
              <strong className="canvas-row-card__title">
                {get(connector, "label") ||
                  item.connectorKey ||
                  listLabels.UNSET}
              </strong>
              {get(connector, "vendor") ? (
                <span className="canvas-row-card__vendor">
                  {connector.vendor}
                </span>
              ) : null}
              <Dropdown
                trigger={["click"]}
                menu={{
                  items: [
                    {
                      key: "edit",
                      label: editing[index]
                        ? listLabels.CLOSE
                        : listLabels.EDIT,
                    },
                    { key: "delete", label: listLabels.DELETE, danger: true },
                  ],
                  onClick: ({ key }) =>
                    key === "edit" ? toggle(index) : remove(index),
                }}
              >
                <button
                  type="button"
                  className="canvas-next__more"
                  aria-label={listLabels.ACTIONS}
                >
                  <CanvasIcon
                    path={ICON_PATHS.dots}
                    size={18}
                    strokeWidth={3}
                  />
                </button>
              </Dropdown>
            </div>
            {failureLabels ? (
              <FailureModeField
                config={item.config}
                labels={failureLabels}
                onChange={(config) => update(index, { config })}
              />
            ) : null}
            {editing[index] ? (
              <div className="canvas-row-card__editor">
                <ConnectorField
                  value={item.connectorKey || null}
                  onChange={(connectorKey) => update(index, { connectorKey })}
                  labels={labels}
                />
                {connector ? (
                  <ConnectorMappings
                    connector={connector}
                    config={item.config}
                    labels={labels}
                    onChange={(patch) => setConfig(index, patch)}
                  />
                ) : null}
                {!connector && item.connectorKey ? (
                  <>
                    <div className="canvas-editor__card-title">
                      {labels.INPUTS}
                    </div>
                    <MappingList
                      value={get(item, "config.inputs")}
                      leftKey="name"
                      rightKey="from"
                      leftLabel={labels.MAP_NAME}
                      rightLabel={labels.MAP_FROM}
                      valueLabel={labels.MAP_VALUE}
                      valueHint={labels.MAP_VALUE_HINT}
                      addLabel={labels.ADD_MAPPING}
                      onChange={(inputs) => setConfig(index, { inputs })}
                    />
                    <div className="canvas-editor__card-title">
                      {labels.OUTPUTS}
                    </div>
                    <MappingList
                      value={get(item, "config.outputs")}
                      leftKey="name"
                      rightKey="to"
                      leftLabel={labels.MAP_NAME}
                      rightLabel={labels.MAP_TO}
                      addLabel={labels.ADD_MAPPING}
                      onChange={(outputs) => setConfig(index, { outputs })}
                    />
                  </>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      })}
      <button
        type="button"
        className="canvas-dashed-button canvas-dashed-button--small"
        onClick={add}
      >
        {listLabels.ADD || labels.ADD}
      </button>
    </div>
  );
};

ConnectorListField.defaultProps = {
  listLabels: {},
};

export default ConnectorListField;
